import Vue, * as nsVue from 'vue'
import { Component, Inject, Model, Prop, Watch } from 'vue-property-decorator'
import * as api from './api'
import { ItemType, Node } from './models';
import Menus from './parts/Menus'
import { sleep, genUniqueId } from './utils'
import * as ui from './ui'
import globalState from './globalState'

type Rect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

type Point = {
  x: number;
  y: number;
}

type Size = {
  width: number;
  height: number;
}

interface ItemDefinitions {
  types: { [index: string]: ItemType };
  map: { [index: string]: string };
}

class Portal {
  key: string;
  label: string;
  root: Node | undefined;
}

type FormResult = {
  accepted: boolean;
  data: any;
}

type FormContent = (h: nsVue.CreateElement, commit: (data?: any)=>void, cancel: ()=>void) => nsVue.VNode

Vue.component('PopoverHook', {
  functional: true,
  props: ['content', 'commit', 'cancel'],
  render(h, context) {
    let {props} = context
    return props.content(h, props.commit, props.cancel)
  }
})

@Component
class PopoverConfirm extends Vue {
  @Prop() message: string
  
  private _dispose?: ()=>void
  
  render(h: nsVue.CreateElement): nsVue.VNode {
    let btn = this.$slots.default[0]
    let click = async (...args) => {
      if (this._dispose) {
        this._dispose()
        this._dispose = undefined
        return
      }
      let app = this.$root.$children[0] as App
      let pop = app.popoverConfirm(this.$el, this.message)
      this._dispose = pop.dispose
      let result = await pop.future
      this._dispose = undefined
      if (result) {
        this.$emit('action', ...args)
      }
    }
    if (!btn.data)
      btn.data = {on: {click}}
    else if (!btn.data.on)
      btn.data.on = {click}
    else
      btn.data.on.click = click
    return btn
  }
}

Vue.component('PopoverConfirm', PopoverConfirm)

@Component
class PopoverForm extends Vue {
  @Prop([Object, Function]) view: typeof Vue
  @Prop() props?: {[k: string]: any}

  key = genUniqueId()

  private _dispose?: ()=>void

  render(h: nsVue.CreateElement): nsVue.VNode {
    let content: FormContent = (h, commit, cancel) => {
      let props: {[k: string]: any} = {}
      if (this.props) {
        for (let k in this.props) {
            props[k] = this.props[k]
        }
      }
      props.commit = commit
      props.cancel = cancel
      return h(this.view, {props, key: this.key})
    }

    let btn = this.$slots.default[0]
    let click = async () => {
      if (this._dispose) {
        this._dispose()
        this._dispose = undefined
        return
      }
      let app = this.$root.$children[0] as App
      let pop = app.popoverForm(this.$el, content)
      this._dispose = pop.dispose
      let result = await pop.future
      this._dispose = undefined
      if (result.accepted) {
        this.$emit('action', result.data)
      }
    }
    if (!btn.data)
      btn.data = {on: {click}}
    else if (!btn.data.on)
      btn.data.on = {click}
    else
      btn.data.on.click = click
    return btn
  }
}

Vue.component('PopoverForm', PopoverForm)

let PopoverWrapper: nsVue.FunctionalComponentOptions  = {
  functional: true,
  props: ['renderer', 'dispose'],
  render(h, context) {
    let {props} = context
    return props.renderer(h, context, props.dispose)
  }
}

@Component({
  components: { Menus, PopoverWrapper },
})
export default class App extends Vue {
  itemDefinitions: ItemDefinitions = {
    types: {},
    map: {}
  }

  availableNodeTypes: string[] = []

  portals: Portal[] = []

  uniForms: {type: string, title: string}[] = []

  dbInfo: null | {connectionString: string, username: string, password: string} = null

  initialized: boolean = false

  showMenu: boolean = false

  isStateDirty: boolean = false

  windowSize: Size = {width: 0, height: 0}

  created() {
    let $window = $(window)
    $window.resize(() => {
      this.windowSize.width = $window.width()!
      this.windowSize.height = $window.height()!
    })
    $window.resize()
    if (globalState.auth) {
      this.init()
    }
  }

  async init() {
    let results = await api.batch(['runtime/itemDefinitions', 'runtime/dbInfo', 'portal/roots', 'uniForm/types', 'runtime/availableNodeTypes'])
    this.itemDefinitions.types = results[0].types
    this.itemDefinitions.map = results[0].map
    this.dbInfo = results[1]
    this.portals = results[2]
    this.uniForms = results[3]
    this.availableNodeTypes = results[4]

    this.initialized = true

    // let r = await api.invoke('runtime/itemDefinitions')
    // this.itemDefinitions.types = r.types
    // this.itemDefinitions.map = r.map
    // this.dbInfo = await api.invoke('runtime/dbInfo')
    // this.portals = await api.invoke('portal/roots')
  }

  get shouldRenderView() {
    return this.initialized || this.$route.name == 'login'
  }

  get shouldShowMenuButton() {
    return this.initialized && this.$route.name !== 'login' && this.$route.name !== 'root'
  }

  async getPortalRoot(key: string, force: boolean=false) {
    let p = this.portals.find( v => v.key == key )
    if (!p) {
      throw new Error('invalid key.')
    }
    if (!p.root || force) {
      let root = await api.invoke('portal/tree', key)
      if (!p.root)
        p.root = root
      else
        Object.assign(p.root, root)
    }
    return p.root as Node
  }

  @Watch('showMenu')
  showMenuChanged() {
    if (this.showMenu && (!this.$route || this.$route.name !== 'root')) {
      let f = event => {
        if (!$(event.target!).closest('div.frame-menu>nav').length) {
          this.showMenu = false
          $(document).off('click', f)
        }
      }
      $(document).click(f)
    }
  }

  isModal = false

  exposing: {
    rect: Rect;
    message: string;
    messageHeight: number;
  } | null = null

  get exposeStyle() {
    if (this.exposing) {
      let {left, top, right, bottom} = this.exposing.rect
      let {width, height} = this.windowSize
      return {
        'border-left-width': left + 'px',
        'border-top-width': top + 'px',
        'border-right-width': Math.max(0, width - right) + 'px',
        'border-bottom-width': Math.max(0, height - bottom) + 'px',
      }
    }
  }

  get exposeBtnStyle() {
    const padding = 15
    if (this.exposing && this.exposing.messageHeight) {
      let {top, bottom} = this.exposing.rect
      let {height} = this.windowSize
      if (bottom + this.exposing.messageHeight + padding < height) {
        return {top: (bottom - top + padding) + 'px'}
      }
      else {
        return {bottom: (bottom - top + padding) + 'px'}
      }
    }
    return {visibility: 'hidden'}
  }

  exposeAndConfirm(el: HTMLElement, message = ''): Promise<boolean> {
    let rect = {left: 0, top: 0, right: 0, bottom: 0}
    this.exposing = { rect, message, messageHeight: 0 }
    let ss = () => {
      let {left, top, right, bottom} = el.getBoundingClientRect()
      rect.left = left
      rect.top = top
      rect.right = right
      rect.bottom = bottom
    }
    this.$nextTick(() => {
      ss()
      let el = document.querySelector('div.expose-message')
      if (el && this.exposing) {
        this.exposing.messageHeight = (el as HTMLElement).offsetHeight
      }
    })
    window.addEventListener('resize', ss)
    this.isModal = true
    return new Promise<boolean>(resolve => {
      this.$nextTick(() => {
        let f = (r: boolean) => {
          this.isModal = false
          this.exposing = null
          window.removeEventListener('resize', ss)
          resolve(r)
        }
        $('#exposeOK').one('click', () => f(true))
        $('#exposeCancel').one('click', () => f(false))
      })
    })
  }

  popoverInstance: {
    position: Point;
    content: FormContent;
    commit: (data: any) => void;
    dispose: () => void;
  } | null = null

  popoverForm(el: HTMLElement, content: FormContent): {dispose: ()=>void; future: Promise<FormResult>} {
    if (this.popoverInstance) {
      this.popoverInstance.dispose()
    }
    let bounds = el.getBoundingClientRect()
    let x = Math.ceil(bounds.left + (bounds.right - bounds.left) / 2)
    let y = bounds.bottom + 12
    let position = {x, y}
    let f: (e: JQueryEventObject) => void
    let r: (value: FormResult) => void
    let disposed = false
    let dispose = () => {
      if (!disposed) {
        this.popoverInstance = null
        document.removeEventListener('click', f)
        r({accepted: false, data: undefined})
        disposed = true
      }
    }
    let commit = (data: any) => {
      if (!disposed) {
        this.popoverInstance = null
        document.removeEventListener('click', f)
        r({accepted: true, data})
        disposed = true
      }
    }
    f = event => {
      if (!$(event.target).closest('div.frame-popover').length) {
        dispose()
      }
    }
    this.popoverInstance = {position, content, commit, dispose}
    this.$nextTick(() => {
      let width = $('div.frame-popover').width()!
      this.popoverInstance!.position.x -= Math.ceil(width / 2)
      setTimeout(()=> {
        document.addEventListener('click', f)
      }, 0)
    })
    return {
      dispose,
      future: new Promise<FormResult>(resolve => {
        r = resolve
      })
    }
  }

  popoverConfirm(el: HTMLElement, message: string): {dispose: ()=>void; future: Promise<boolean>} {
    let content: FormContent = (h, commit, cancel) => {
      let title = h('div', {staticClass: 'title'}, message)
      let btnCancel = h('button', {on: {click: e => cancel()}}, '取消')
      let btnOK = h('button', {staticClass: 'light', on: {click: e => commit()}}, '确认')
      let btns = h('div', {staticClass: 'btns'}, [ btnCancel, btnOK ])
      return h('div', { staticClass: 'popover-confirm'}, [title, btns])
    }
    let p = this.popoverForm(el, content)
    return {
      dispose: p.dispose,
      future: new Promise(resolve => {
        p.future.then(v => resolve(v.accepted))
      })
    }
  }

  uiState = ui.state

  @Watch('uiState.popover')
  PopoverInstanceChanged() {
    let popover = this.uiState.popover
    let f = (event: Event) => {
      if (!$(event.target!).closest('div.popover-wrapper').length) {
        if (popover)
          popover.dispose()
      }
    }
    if (popover === null) {
      document.removeEventListener('click', f)
    }
    else {
      this.$nextTick(() => {
        if (popover && popover.onShow) {
          let e = {position: popover.position, target: document.querySelector('div.popover-wrapper')!}
          popover.onShow(e)
          if (popover.position.x !== e.position.x || popover.position.y !== e.position.y) {
            popover.position = e.position
          }
        }
      })
      setTimeout(()=> {
        document.addEventListener('click', f)
      }, 0)
    }
  }
}
